import { useEffect, useRef, useState } from "react";
import RightTimeApi from "@/API";
import "@style/Admin.scss";
import { toast } from "react-toastify";
import { prefetchWhenIdle } from "@/utils/runWhenIdle";

import SiteAccess from "../elements/tables/SiteAccess";
import RolePermissionsTable from "../elements/tables/RolePermissionsTable";
import TestTypeInfoModal from "@modals/TestTypeInfoModal";
import RequestFormFieldsTable from "../elements/tables/RequestFormFieldsTable";
import TestStatuses from "../elements/tables/TestStatuses";
import TestList from "../elements/tables/TestList";
import TestTypeDepartmentsTable from "../elements/tables/TestTypeDepartmentsTable";

function emptyListItem(item: any) {
  return {
    ...item,
    errors: item.errors ?? [],
    fields: item.fields ?? [],
    sites: item.sites ?? [],
    statuses: item.statuses ?? [],
    billableStatuses: item.billableStatuses ?? [],
    archiveStatuses: item.archiveStatuses ?? [],
  };
}

function mergeTestTypeDetail(listItem: any, detail: any) {
  return emptyListItem({
    ...listItem,
    ...detail,
    companyTestType: {
      ...listItem.companyTestType,
      ...detail.companyTestType,
    },
    errors: listItem.errors?.length ? listItem.errors : (detail.errors ?? []),
  });
}

export default function CompanyTestTypesTab(props: any) {
  const [data, updateData] = useState<any>({ testTypes: [] });
  const [activeTestType, updateActiveTestType] = useState<any>(null);
  const [lookups, updateLookups] = useState<any>(null);
  const [selectedCttId, updateSelectedCttId] = useState<any>(null);
  const [contextLoaded, setContextLoaded] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [loadedDetailIds, setLoadedDetailIds] = useState<Set<number>>(() => new Set());
  const validationRequestRef = useRef(0);
  const countsRequestRef = useRef(0);
  const contextRequestRef = useRef(0);
  const isOnThisTab = props?.tabData?.activeTab === "tests";

  useEffect(() => {
    if (isOnThisTab) {
      loadInitialAsync();
    }
  }, [props?.tabData?.activeTab]);

  if (!isOnThisTab) return null;

  async function prefetchContextAsync(companyId: number) {
    if (contextLoaded) return;
    const requestId = ++contextRequestRef.current;
    try {
      const resp = await RightTimeApi.getAsync(`/CompanyTestTypesTab/${companyId}/context`);
      if (requestId !== contextRequestRef.current || !resp.ok) return;
      updateData((prev: any) => ({
        ...prev,
        ...resp.jsonData,
        testTypes: prev.testTypes,
      }));
      setContextLoaded(true);
    } catch (error) {
      console.error("Error prefetching CTT context:", error);
    }
  }

  async function loadCountsAsync(companyId: number) {
    const requestId = ++countsRequestRef.current;
    try {
      const resp = await RightTimeApi.getAsync(`/CompanyTestTypesTab/${companyId}/counts`);
      if (requestId !== countsRequestRef.current || !resp.ok) return;

      const testCountsByCttId = resp.jsonData?.testCountsByCttId ?? {};
      updateData((prev: any) => ({
        ...prev,
        countsLoaded: true,
        testTypes: (prev.testTypes ?? []).map((tt: any) => {
          const cttId = tt.companyTestType.id;
          const count = testCountsByCttId[cttId] ?? testCountsByCttId[String(cttId)];
          return {
            ...tt,
            testCount: typeof count === "number" ? count : 0,
          };
        }),
      }));

      updateActiveTestType((current: any) => {
        if (!current?.companyTestType?.id) return current;
        const cttId = current.companyTestType.id;
        const count = testCountsByCttId[cttId] ?? testCountsByCttId[String(cttId)];
        if (typeof count !== "number") return current;
        return { ...current, testCount: count };
      });
    } catch (error) {
      console.error("Error fetching CTT counts:", error);
    }
  }

  async function loadValidationAsync(companyId: number) {
    const requestId = ++validationRequestRef.current;
    try {
      const resp = await RightTimeApi.getAsync(`/CompanyTestTypesTab/${companyId}/validation`);
      if (requestId !== validationRequestRef.current || !resp.ok) return;

      const errorsByCttId = resp.jsonData?.errorsByCttId ?? {};
      updateData((prev: any) => ({
        ...prev,
        testTypes: (prev.testTypes ?? []).map((tt: any) => {
          const cttId = tt.companyTestType.id;
          const errors = errorsByCttId[cttId] ?? errorsByCttId[String(cttId)] ?? [];
          return { ...tt, errors };
        }),
      }));

      updateActiveTestType((current: any) => {
        if (!current?.companyTestType?.id) return current;
        const cttId = current.companyTestType.id;
        const errors = errorsByCttId[cttId] ?? errorsByCttId[String(cttId)];
        if (!errors) return current;
        return { ...current, errors };
      });
    } catch (error) {
      console.error("Error fetching CTT validation:", error);
    }
  }

  async function loadInitialAsync() {
    try {
      props?.tabData?.updateSpinner?.(true);

      const companyId = props?.tabData?.company?.id;
      const listStartedAt = performance.now();
      const [listResp, lookupsResp] = await Promise.all([
        RightTimeApi.getAsync(`/CompanyTestTypesTab/${companyId}/list`),
        RightTimeApi.getAsync(`/TestLookups/${companyId}`),
      ]);

      props?.tabData?.updateSpinner?.(false);

      if (lookupsResp?.ok) {
        updateLookups(lookupsResp.jsonData);
      }

      if (listResp.ok) {
        const testTypes = (listResp.jsonData.testTypes ?? []).map(emptyListItem);
        updateData({ testTypes, countsLoaded: false });
        updateActiveTestType(null);
        setContextLoaded(false);
        setLoadedDetailIds(new Set());
        loadCountsAsync(companyId);
        loadValidationAsync(companyId);
        prefetchWhenIdle(() => prefetchContextAsync(companyId));
        if (import.meta.env.DEV) {
          console.info(`[perf] CTT list visible in ${Math.round(performance.now() - listStartedAt)}ms`);
        }
      } else {
        toast.error("Failed to fetch data");
      }
    } catch (error) {
      props?.tabData?.updateSpinner?.(false);
      console.error("Error fetching data:", error);
      toast.error("Error fetching data");
    }
  }

  async function getDataAsync(selectCttId?: number) {
    try {
      props?.tabData?.updateSpinner?.(true);

      const companyId = props?.tabData?.company?.id;
      const selectedId = selectCttId ?? activeTestType?.companyTestType?.id ?? null;
      const resp = await RightTimeApi.getAsync(`/CompanyTestTypesTab/${companyId}`);
      const lookupsResp = await RightTimeApi.getAsync(`/TestLookups/${companyId}`);

      props?.tabData?.updateSpinner?.(false);

      if (lookupsResp?.ok) {
        updateLookups(lookupsResp.jsonData);
      }

      if (resp.ok) {
        const refreshedTestTypes = resp.jsonData.testTypes ?? [];
        updateData({ ...resp.jsonData, countsLoaded: true });
        setContextLoaded(true);
        setLoadedDetailIds(new Set(
          refreshedTestTypes.map((tt: any) => tt.companyTestType.id)
        ));

        if (selectedId) {
          const selected = refreshedTestTypes.find(
            (tt: any) => tt.companyTestType.id === selectedId
          );
            if (selected) {
            if (selectCttId) {
              await selectTestTypeAsync(selectCttId, refreshedTestTypes, true);
            } else {
              updateActiveTestType(selected);
            }
          } else {
            updateActiveTestType(null);
          }
        } else {
          updateActiveTestType(null);
        }
      } else {
        toast.error("Failed to fetch data");
      }
    } catch (error) {
      props?.tabData?.updateSpinner?.(false);
      console.error("Error fetching data:", error);
      toast.error("Error fetching data");
    }
  }

  async function selectTestTypeAsync(testTypeId: number, testTypesOverride?: any[], forceDetailLoad = false) {
    const sourceTestTypes = testTypesOverride ?? data.testTypes;
    const listItem = sourceTestTypes?.find((r: any) => r.companyTestType.id === testTypeId);
    if (!listItem) return;

    if (activeTestType?.companyTestType?.id === testTypeId && !forceDetailLoad) {
      updateSelectedCttId(testTypeId);
      return;
    }

    if (!forceDetailLoad && loadedDetailIds.has(testTypeId)) {
      updateActiveTestType(listItem);
      return;
    }

    const companyId = props?.tabData?.company?.id;
    setDetailLoading(true);

    try {
      const detailPromise = RightTimeApi.getAsync(`/CompanyTestTypesTab/${companyId}/ctt/${testTypeId}`);
      const contextPromise = contextLoaded
        ? Promise.resolve(null)
        : RightTimeApi.getAsync(`/CompanyTestTypesTab/${companyId}/context`);

      const [detailResp, contextResp] = await Promise.all([detailPromise, contextPromise]);

      if (contextResp?.ok) {
        updateData((prev: any) => ({
          ...prev,
          ...contextResp.jsonData,
          testTypes: prev.testTypes,
        }));
        setContextLoaded(true);
      }

      if (!detailResp?.ok) {
        toast.error("Failed to load test type details");
        return;
      }

      const merged = mergeTestTypeDetail(listItem, detailResp.jsonData);
      updateData((prev: any) => ({
        ...prev,
        testTypes: (prev.testTypes ?? []).map((tt: any) =>
          tt.companyTestType.id === testTypeId ? merged : tt
        ),
      }));
      setLoadedDetailIds((prev) => new Set(prev).add(testTypeId));
      updateActiveTestType(merged);
    } catch (error) {
      console.error("Error loading CTT detail:", error);
      toast.error("Error loading test type details");
    } finally {
      setDetailLoading(false);
    }
  }

  function updateActiveTestTypeId(testTypeId: number) {
    selectTestTypeAsync(testTypeId);
  }

  async function refreshDepartmentLinksAsync() {
    const companyId = props?.tabData?.company?.id;
    if (!companyId) return;
    try {
      const resp = await RightTimeApi.getAsync(`/CompanyTestTypesTab/${companyId}/context`);
      if (resp?.ok && resp.jsonData) {
        updateData((prev: any) => ({
          ...prev,
          departments: resp.jsonData.departments ?? [],
          testTypeDepartments: resp.jsonData.testTypeDepartments ?? [],
        }));
      }
    } catch (error) {
      console.error("Error refreshing CTT department links:", error);
    }
  }

  const departmentsEnabled = !!props?.tabData?.company?.departmentsEnabled;

  return (
    <>
      <div className="companyTestTypesTab">
        <div className="horizontal">
          <TestList
            data={data}
            activeTestType={activeTestType}
            updateActiveTestTypeId={updateActiveTestTypeId}
            updateData={getDataAsync}
            tabData={props?.tabData}
            countsLoaded={data.countsLoaded === true}
          />
          {detailLoading && (
            <div className="cttDetailLoading">Loading test type details…</div>
          )}
          {activeTestType && !detailLoading && (
            <>
              <div>
                <RequestFormFieldsTable
                  data={data}
                  activeTestType={activeTestType}
                  tabData={props?.tabData}
                  updateData={getDataAsync}
                />
              </div>
              {lookups?.testStatuses && (
                <div>
                  <TestStatuses
                    testStatuses={lookups.testStatuses}
                    activeTestType={activeTestType}
                    updateData={getDataAsync}
                  />
                </div>
              )}
            </>
          )}
        </div>
        {activeTestType && !detailLoading && (
          <RolePermissionsTable
            data={data}
            lookups={lookups}
            activeTestType={activeTestType}
            updateData={getDataAsync}
          />
        )}
        {activeTestType && !detailLoading && (
          <SiteAccess
            data={data}
            lookups={lookups}
            activeTestType={activeTestType}
            siteEntityName={props?.tabData?.company?.subEntityTypeName}
            updateData={getDataAsync}
            updateSpinner={props?.tabData?.updateSpinner}
          />
        )}
        {departmentsEnabled && activeTestType && !detailLoading && (
          <TestTypeDepartmentsTable
            testType={activeTestType.companyTestType}
            departments={data.departments}
            testTypeDepartments={data.testTypeDepartments}
            adminPerms={props?.tabData?.adminPerms}
            refreshLinks={refreshDepartmentLinksAsync}
          />
        )}
      </div>

      <TestTypeInfoModal
        onClose={() => { updateSelectedCttId(null); }}
        cttId={selectedCttId}
        data={data}
        lookups={lookups}
        updateData={getDataAsync}
        updateSpinner={props?.tabData?.updateSpinner}
        tabData={props?.tabData}
      />
    </>
  );
}
